import { useState, useRef, useEffect } from 'react';

interface ImportDropdownProps {
  onExcelImport: () => void;
  onPaste: () => void;
  onBulkEdit: () => void;
  onDownloadTemplate: () => void;
}

export default function ImportDropdown({
  onExcelImport,
  onPaste,
  onBulkEdit,
  onDownloadTemplate,
}: ImportDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const handleOption = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="btn-outline flex items-center gap-1.5 text-sm"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
        Import / Edit
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-500 rounded-lg shadow-lg z-30">
          <div className="py-1">
            <button
              onClick={() => handleOption(onExcelImport)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-600 flex items-start gap-3"
            >
              <svg className="w-5 h-5 text-accent-aquamarine mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Excel/CSV Import</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Upload a spreadsheet file</div>
              </div>
            </button>

            <button
              onClick={() => handleOption(onPaste)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-600 flex items-start gap-3"
            >
              <svg className="w-5 h-5 text-accent-blue mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Paste from Excel</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Paste copied cells</div>
              </div>
            </button>

            <button
              onClick={() => handleOption(onBulkEdit)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-600 flex items-start gap-3"
            >
              <svg className="w-5 h-5 text-accent-orange mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Bulk Edit</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Edit all sprints in a table</div>
              </div>
            </button>

            <div className="border-t border-gray-200 dark:border-dark-500 my-1" />

            <button
              onClick={() => handleOption(onDownloadTemplate)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-600 flex items-start gap-3"
            >
              <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Download Template</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Pre-formatted Excel file</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
